import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LongPage, TocEntry } from "./SideNav";
import {
  Bullet,
  Bullets,
  DefItem,
  DefList,
  Ext,
  Mail,
  Note,
  PageHeader,
  Pull,
  Section,
  SimpleTable,
  SubHeading,
} from "./ui";
import { CONTACT_EMAIL, LINKS } from "./constants";

export const AboutPage: React.FC = () => {
  const { t } = useTranslation("aboutPage");

  const TOC: TocEntry[] = [
    { id: "purpose", label: t("toc.purpose") },
    { id: "roles", label: t("toc.roles") },
    { id: "getting-started", label: t("toc.gettingStarted") },
    { id: "researchers", label: t("toc.researchers") },
    { id: "leading", label: t("toc.leading") },
    { id: "uploading", label: t("toc.uploading") },
    { id: "worth-knowing", label: t("toc.worthKnowing") },
    { id: "trouble", label: t("toc.trouble") },
    { id: "help", label: t("toc.help") },
  ];

  return (
    <LongPage
      toc={TOC}
      intro={
        <PageHeader
          kicker={t("header.kicker")}
          title={t("header.title")}
          lead={
            <>
              <p className="mb-4">{t("header.lead1")}</p>
              <p>
                {t("header.lead2a")}
                <Link to="/security" className="text-accent underline underline-offset-2">
                  {t("header.lead2SecurityLink")}
                </Link>
                {t("header.lead2b")}
                <Link to="/roadmap" className="text-accent underline underline-offset-2">
                  {t("header.lead2RoadmapLink")}
                </Link>
                {t("header.lead2c")}
              </p>
            </>
          }
        />
      }
    >
      <div className="divide-y divide-sand-200 [&>section:not(:first-child)]:pt-14 [&>section]:pb-14 [&>section:last-child]:pb-0">
        <Section id="purpose" number="01" title={t("purpose.title")}>
          <p>{t("purpose.p1")}</p>
          <p>
            {t("purpose.p2a")}
            <strong>{t("purpose.p2Strong")}</strong>
            {t("purpose.p2b")}
          </p>

          <Pull>{t("purpose.pull")}</Pull>

          <Bullets>
            <Bullet>
              {t("purpose.bullet1a")}
              <em>{t("purpose.bullet1Em")}</em>
              {t("purpose.bullet1b")}
              <code>bK9x…</code>
              {t("purpose.bullet1c")}
            </Bullet>
            <Bullet>{t("purpose.bullet2")}</Bullet>
          </Bullets>

          <p>{t("purpose.p3")}</p>

          <SubHeading>{t("purpose.notTitle")}</SubHeading>
          <Bullets>
            <Bullet>
              <strong>{t("purpose.not1Strong")}</strong>
              {t("purpose.not1Rest")}
            </Bullet>
            <Bullet>
              <strong>{t("purpose.not2Strong")}</strong>
              {t("purpose.not2a")}
              <Ext href={LINKS.gdpr}>GDPR</Ext>
              {t("purpose.not2b")}
            </Bullet>
            <Bullet>
              <strong>{t("purpose.not3Strong")}</strong>
              {t("purpose.not3Rest")}
            </Bullet>
          </Bullets>
        </Section>

        <Section id="roles" number="02" title={t("roles.title")}>
          <p>{t("roles.p1")}</p>

          <DefList>
            <DefItem term={t("roles.researcherTerm")}>{t("roles.researcherBody")}</DefItem>
            <DefItem term={t("roles.piTerm")}>{t("roles.piBody")}</DefItem>
            <DefItem term={t("roles.managerTerm")}>
              {t("roles.managerBodya")}
              <strong>{t("roles.managerStrong")}</strong>
              {t("roles.managerBodyb")}
            </DefItem>
            <DefItem term={t("roles.providerTerm")}>{t("roles.providerBody")}</DefItem>
          </DefList>

          <SubHeading>{t("roles.deptHeading")}</SubHeading>
          <p>
            {t("roles.deptP1a")}
            <strong>{t("roles.deptStrong")}</strong>
            {t("roles.deptP1b")}
          </p>
          <p>{t("roles.deptP2")}</p>
          <Note tone="accent" title={t("roles.newDeptTitle")}>
            <p>
              {t("roles.newDeptA")}
              <Mail address={CONTACT_EMAIL} subject="New department in Nafnleyndarkerfi" />
              {t("roles.newDeptB")}
            </p>
          </Note>
          <Note tone="neutral">
            <p>
              {t("roles.wordingA")}
              <em>{t("roles.wordingEm")}</em>
              {t("roles.wordingB")}
            </p>
          </Note>
        </Section>

        <Section id="getting-started" number="03" title={t("gettingStarted.title")}>
          <SubHeading>{t("gettingStarted.accountHeading")}</SubHeading>
          <p>
            {t("gettingStarted.accountP1a")}
            <strong>{t("gettingStarted.accountStrong")}</strong>
            {t("gettingStarted.accountP1b")}
          </p>
          <p>
            {t("gettingStarted.pwP2a")}
            <Ext href={LINKS.pwnedPasswords}>Pwned Passwords</Ext>
            {t("gettingStarted.pwP2b")}
          </p>

          <SubHeading>{t("gettingStarted.twoFaHeading")}</SubHeading>
          <p>{t("gettingStarted.twoFaP1")}</p>
          <p>
            {t("gettingStarted.totpA")}
            <Ext href={LINKS.totpRfc}>{t("gettingStarted.totpLabel")}</Ext>
            {t("gettingStarted.totpB")}
          </p>
          <Bullets tight>
            <Bullet>
              <Ext href={LINKS.googleAuthenticator}>Google Authenticator</Ext>
              {t("gettingStarted.googleAuthRest")}
            </Bullet>
            <Bullet>
              <Ext href={LINKS.microsoftAuthenticator}>Microsoft Authenticator</Ext>
              {t("gettingStarted.msAuthRest")}
            </Bullet>
            <Bullet>
              <Ext href={LINKS.authy}>Authy</Ext>
              {t("gettingStarted.authyRest")}
            </Bullet>
            <Bullet>
              <Ext href={LINKS.onePassword}>1Password</Ext>
              {t("gettingStarted.onePasswordRest")}
            </Bullet>
            <Bullet>
              <Ext href={LINKS.freeOtp}>FreeOTP</Ext>
              {t("gettingStarted.freeOtpRest")}
            </Bullet>
          </Bullets>
          <Note tone="caution" title={t("gettingStarted.recoveryTitle")}>
            <p>{t("gettingStarted.recoveryBody")}</p>
          </Note>

          <SubHeading>{t("gettingStarted.loginHeading")}</SubHeading>
          <p>{t("gettingStarted.loginP1")}</p>
          <p>
            {t("gettingStarted.sessionA")}
            <em>{t("gettingStarted.sessionEm")}</em>
            {t("gettingStarted.sessionB")}
          </p>

          <SubHeading>{t("gettingStarted.forgotHeading")}</SubHeading>
          <p>{t("gettingStarted.forgotP1")}</p>
          <p>
            {t("gettingStarted.changePwA")}
            <em>{t("gettingStarted.changePwEm")}</em>
            {t("gettingStarted.changePwB")}
          </p>
        </Section>

        <Section id="researchers" number="04" title={t("researchers.title")}>
          <SubHeading>{t("researchers.projectsHeading")}</SubHeading>
          <p>{t("researchers.projectsBody")}</p>

          <SubHeading>{t("researchers.catalogueHeading")}</SubHeading>
          <p>{t("researchers.catalogueBody")}</p>
          <Bullets tight>
            <Bullet>{t("researchers.bulletName")}</Bullet>
            <Bullet>{t("researchers.bulletRows")}</Bullet>
            <Bullet>{t("researchers.bulletColumns")}</Bullet>
            <Bullet>{t("researchers.bulletExpiry")}</Bullet>
            <Bullet>{t("researchers.bulletDownloadable")}</Bullet>
          </Bullets>
          <Note tone="accent" title={t("researchers.sensitiveTitle")}>
            <p>{t("researchers.sensitiveBody")}</p>
          </Note>

          <p>{t("researchers.twoSettingsIntro")}</p>
          <SimpleTable
            head={[t("researchers.tableHead0"), t("researchers.tableHead1")]}
            rows={[
              [t("researchers.tableVisibilityTerm"), t("researchers.tableVisibilityBody")],
              [t("researchers.tableDownloadTerm"), t("researchers.tableDownloadBody")],
            ]}
          />
          <p>{t("researchers.visibleNotDownloadable")}</p>

          <SubHeading>{t("researchers.downloadHeading")}</SubHeading>
          <p>
            {t("researchers.downloadA")}
            <strong>{t("researchers.downloadStrong")}</strong>
            {t("researchers.downloadB")}
          </p>

          <SubHeading>{t("researchers.bulkHeading")}</SubHeading>
          <p>{t("researchers.bulkIntro")}</p>
          <Bullets tight>
            <Bullet>{t("researchers.bulkCsv")}</Bullet>
            <Bullet>
              {t("researchers.bulkJupyterA")}
              <Ext href={LINKS.jupyter}>Jupyter</Ext>
              {t("researchers.bulkJupyterB")}
            </Bullet>
            <Bullet>
              {t("researchers.bulkRA")}
              <Ext href={LINKS.rProject}>R</Ext>
              {t("researchers.bulkRB")}
            </Bullet>
            <Bullet>{t("researchers.bulkReadme")}</Bullet>
          </Bullets>
          <p>{t("researchers.bulkOutro")}</p>

          <SubHeading>{t("researchers.requestHeading")}</SubHeading>
          <p>
            {t("researchers.requestA")}
            <strong>{t("researchers.requestStrong")}</strong>
            {t("researchers.requestB")}
          </p>
          <p>{t("researchers.requestFlow")}</p>
          <SimpleTable
            head={[t("researchers.statusTableHead0"), t("researchers.statusTableHead1")]}
            rows={[
              [t("researchers.statusPending"), t("researchers.statusPendingBody")],
              [t("researchers.statusApproved"), t("researchers.statusApprovedBody")],
              [t("researchers.statusRejected"), t("researchers.statusRejectedBody")],
              [t("researchers.statusRedirected"), t("researchers.statusRedirectedBody")],
              [t("researchers.statusFulfilled"), t("researchers.statusFulfilledBody")],
            ]}
          />

          <SubHeading>{t("researchers.pseudoFileHeading")}</SubHeading>
          <p>{t("researchers.pseudoFileBody")}</p>

          <SubHeading>{t("researchers.uploadIntoHeading")}</SubHeading>
          <p>
            {t("researchers.uploadIntoA")}
            <a href="#uploading">{t("researchers.uploadIntoLink")}</a>
            {t("researchers.uploadIntoB")}
          </p>
        </Section>

        <Section id="leading" number="05" title={t("leading.title")}>
          <SubHeading>{t("leading.createHeading")}</SubHeading>
          <p>{t("leading.createBody")}</p>

          <SubHeading>{t("leading.bringingHeading")}</SubHeading>
          <p>{t("leading.bringingP1")}</p>
          <p>{t("leading.bringingP2")}</p>

          <SubHeading>{t("leading.settingsHeading")}</SubHeading>
          <p>{t("leading.settingsIntro")}</p>
          <DefList>
            <DefItem term={t("leading.resAccessTerm")}>
              {t("leading.resAccessA")}
              <em>{t("leading.resAccessEm1")}</em>
              {t("leading.resAccessMid")}
              <em>{t("leading.resAccessEm2")}</em>
              {t("leading.resAccessB")}
            </DefItem>
            <DefItem term={t("leading.catVisTerm")}>{t("leading.catVisBody")}</DefItem>
            <DefItem term={t("leading.notifyTerm")}>{t("leading.notifyBody")}</DefItem>
          </DefList>
          <p>{t("leading.settingsOutro")}</p>

          <SubHeading>{t("leading.reviewHeading")}</SubHeading>
          <p>{t("leading.reviewP1")}</p>
          <Bullets tight>
            <Bullet>
              <strong>{t("leading.approveStrong")}</strong>
              {t("leading.approveRest")}
            </Bullet>
            <Bullet>
              <strong>{t("leading.rejectStrong")}</strong>
              {t("leading.rejectRest")}
            </Bullet>
            <Bullet>
              <strong>{t("leading.redirectStrong")}</strong>
              {t("leading.redirectRest")}
            </Bullet>
          </Bullets>
          <p>
            {t("leading.wherevA")}
            <strong>{t("leading.wherevStrong")}</strong>
            {t("leading.wherevB")}
          </p>

          <SubHeading>{t("leading.forwardHeading")}</SubHeading>
          <p>{t("leading.forwardBody")}</p>

          <SubHeading>{t("leading.overseeHeading")}</SubHeading>
          <p>{t("leading.overseeBody")}</p>
          <Note tone="accent" title={t("leading.oversightTitle")}>
            <p>{t("leading.oversightBody")}</p>
          </Note>

          <SubHeading>{t("leading.managingHeading")}</SubHeading>
          <p>{t("leading.managingIntro")}</p>
          <Bullets tight>
            <Bullet>{t("leading.managingPromote")}</Bullet>
            <Bullet>{t("leading.managingDisable")}</Bullet>
            <Bullet>{t("leading.managingReenable")}</Bullet>
            <Bullet>{t("leading.managingEndSessions")}</Bullet>
            <Bullet>{t("leading.managingReset2fa")}</Bullet>
          </Bullets>

          <SubHeading>{t("leading.announceHeading")}</SubHeading>
          <p>{t("leading.announceBody")}</p>
        </Section>

        <Section id="uploading" number="06" title={t("uploading.title")}>
          <p>
            {t("uploading.p1a")}
            <strong>{t("uploading.p1Strong")}</strong>
            {t("uploading.p1b")}
          </p>

          <SubHeading>{t("uploading.reviewStepHeading")}</SubHeading>
          <p>{t("uploading.reviewStepP1")}</p>
          <p>
            <strong>{t("uploading.idColStrong")}</strong>
            {t("uploading.idColA")}
            <code>kennitala</code>, <code>nafn</code>, <code>heimilisfang</code>,{" "}
            <code>email</code>
            {t("uploading.idColB")}
          </p>
          <p>
            <strong>{t("uploading.freeTextStrong")}</strong>
            {t("uploading.freeTextA")}
            <em>{t("uploading.freeTextEm")}</em>
            {t("uploading.freeTextB")}
          </p>
          <p>{t("uploading.findingIntro")}</p>
          <Bullets tight>
            <Bullet>
              <strong>{t("uploading.replaceStrong")}</strong>
              {t("uploading.replaceA")}
              <em>{t("uploading.replaceEm")}</em>
              {t("uploading.replaceB")}
            </Bullet>
            <Bullet>
              <strong>{t("uploading.dropStrong")}</strong>
              {t("uploading.dropRest")}
            </Bullet>
          </Bullets>
          <Note tone="caution" title={t("uploading.readScreenTitle")}>
            <p>{t("uploading.readScreenBody")}</p>
          </Note>

          <SubHeading>{t("uploading.settingAccessHeading")}</SubHeading>
          <p>{t("uploading.settingAccessBody")}</p>

          <SubHeading>{t("uploading.externalHeading")}</SubHeading>
          <p>{t("uploading.externalIntro")}</p>
          <Bullets tight>
            <Bullet>
              <strong>{t("uploading.reqDirStrong")}</strong>
              {t("uploading.reqDirRest")}
            </Bullet>
            <Bullet>
              <strong>{t("uploading.acceptedStrong")}</strong>
              {t("uploading.acceptedRest")}
            </Bullet>
            <Bullet>
              <strong>{t("uploading.activityStrong")}</strong>
              {t("uploading.activityRest")}
            </Bullet>
          </Bullets>
          <p>{t("uploading.externalOutro")}</p>
        </Section>

        <Section id="worth-knowing" number="07" title={t("worthKnowing.title")}>
          <SubHeading>{t("worthKnowing.expiryHeading")}</SubHeading>
          <p>
            {t("worthKnowing.expiryP1a")}
            <em>{t("worthKnowing.expiryP1Em")}</em>
            {t("worthKnowing.expiryP1b")}
          </p>
          <p>
            {t("worthKnowing.expiryP2a")}
            <strong>{t("worthKnowing.expiryP2Strong")}</strong>
            {t("worthKnowing.expiryP2b")}
          </p>
          <p>{t("worthKnowing.expiryP3")}</p>

          <SubHeading>{t("worthKnowing.notDeleteHeading")}</SubHeading>
          <p>
            {t("worthKnowing.notDeleteA")}
            <strong>{t("worthKnowing.notDeleteStrong")}</strong>
            {t("worthKnowing.notDeleteB")}
          </p>
          <p>{t("worthKnowing.notDeleteP2")}</p>
          <p>
            {t("worthKnowing.plainlyA")}
            <strong>{t("worthKnowing.plainlyStrong")}</strong>
            {t("worthKnowing.plainlyB")}
          </p>
          <Note tone="accent" title={t("worthKnowing.trustTitle")}>
            <p>{t("worthKnowing.trustBody")}</p>
          </Note>

          <SubHeading>{t("worthKnowing.loggedHeading")}</SubHeading>
          <p>{t("worthKnowing.loggedBody")}</p>

          <SubHeading>{t("worthKnowing.rateLimitHeading")}</SubHeading>
          <p>{t("worthKnowing.rateLimitBody")}</p>

          <SubHeading>{t("worthKnowing.notificationsHeading")}</SubHeading>
          <p>{t("worthKnowing.notificationsBody")}</p>

          <SubHeading>{t("worthKnowing.reversibleHeading")}</SubHeading>
          <p>
            {t("worthKnowing.reversibleA")}
            <Link to="/security/technical#review" className="text-accent underline underline-offset-2">
              {t("worthKnowing.reversibleLink")}
            </Link>
            {t("worthKnowing.reversibleB")}
          </p>
        </Section>

        <Section id="trouble" number="08" title={t("trouble.title")}>
          <SimpleTable
            head={[t("trouble.tableHead0"), t("trouble.tableHead1")]}
            rows={[
              [t("trouble.row1Situation"), t("trouble.row1Action")],
              [t("trouble.row2Situation"), t("trouble.row2Action")],
              [t("trouble.row3Situation"), t("trouble.row3Action")],
              [t("trouble.row4Situation"), t("trouble.row4Action")],
              [t("trouble.row5Situation"), t("trouble.row5Action")],
              [t("trouble.row6Situation"), t("trouble.row6Action")],
              [t("trouble.row7Situation"), t("trouble.row7Action")],
              [t("trouble.row8Situation"), t("trouble.row8Action")],
            ]}
          />
        </Section>

        <Section id="help" number="09" title={t("help.title")}>
          <p>{t("help.p1")}</p>
          <p>
            {t("help.p2a")}
            <Mail address={CONTACT_EMAIL} />
            {t("help.p2b")}
          </p>
          <p>{t("help.p3")}</p>
        </Section>
      </div>
    </LongPage>
  );
};
